/**
 * FilterPopover — panel multi-group (modified + types) stil codex-v6.
 * Folosește SegmentedButton pentru modified + checkboxes pentru tipuri.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SegmentedButton from '@/components/ui/SegmentedButton';
import { CRED_TYPES } from '@/features/vault/CredentialForm';
import type { CredentialType } from '@/api/credentials';

export type ModifiedFilter = 'all' | 'today' | 'week' | 'month';

interface Props {
  modified: ModifiedFilter;
  onModifiedChange: (value: ModifiedFilter) => void;
  types: Set<CredentialType>;
  onTypesChange: (types: Set<CredentialType>) => void;
  activeCount: number;
  onClearAll: () => void;
}

export default function FilterPopover({
  modified,
  onModifiedChange,
  types,
  onTypesChange,
  activeCount,
  onClearAll,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleType = (type: CredentialType) => {
    const next = new Set(types);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    onTypesChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-md text-[13px] transition-colors"
        style={{
          background: activeCount > 0 ? 'var(--accent-bg)' : 'var(--bg)',
          border: `1px solid ${activeCount > 0 ? 'var(--accent)' : 'var(--border)'}`,
          color: activeCount > 0 ? 'var(--accent)' : 'var(--text-muted)',
        }}
      >
        <FilterIcon />
        <span>{t('vault_ui.filter.title')}</span>
        {activeCount > 0 && (
          <span
            className="font-mono rounded-full px-1.5 text-[10px] font-semibold"
            style={{
              background: 'var(--accent)',
              color: 'var(--bg-elevated)',
              minWidth: 18,
              textAlign: 'center',
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 rounded-lg p-4 space-y-4"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-popover)',
            minWidth: 340,
          }}
        >
          {/* Grupa „Modificat" */}
          <div>
            <div
              className="font-mono uppercase mb-2"
              style={{
                fontSize: 10,
                letterSpacing: '0.14em',
                color: 'var(--text-muted)',
              }}
            >
              {t('vault_ui.filter.modified')}
            </div>
            <SegmentedButton
              options={[
                { value: 'all', label: t('vault.all') },
                { value: 'today', label: t('vault_ui.filter.today') },
                { value: 'week', label: t('vault_ui.filter.week') },
                { value: 'month', label: t('vault_ui.filter.month') },
              ]}
              value={modified}
              onChange={onModifiedChange}
              size="sm"
            />
          </div>

          {/* Grupa „Tip" */}
          <div>
            <div
              className="font-mono uppercase mb-2"
              style={{
                fontSize: 10,
                letterSpacing: '0.14em',
                color: 'var(--text-muted)',
              }}
            >
              {t('vault.col_type')}
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {CRED_TYPES.map((ct) => (
                <label
                  key={ct.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-[12px]"
                  style={{
                    background: types.has(ct.value) ? 'var(--accent-bg)' : 'transparent',
                    color: types.has(ct.value) ? 'var(--accent)' : 'var(--text-muted)',
                  }}
                  onMouseEnter={(e) => {
                    if (!types.has(ct.value))
                      e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    if (!types.has(ct.value))
                      e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <input
                    type="checkbox"
                    checked={types.has(ct.value)}
                    onChange={() => toggleType(ct.value)}
                    className="accent-[var(--accent)]"
                  />
                  <span>{t(ct.labelKey)}</span>
                </label>
              ))}
            </div>
          </div>

          {activeCount > 0 && (
            <div
              className="flex items-center justify-between pt-2"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <span
                className="text-[11px] font-mono"
                style={{ color: 'var(--text-dim)' }}
              >
                {t('vault_ui.filter.active_count', { count: activeCount })}
              </span>
              <button
                type="button"
                onClick={() => {
                  onClearAll();
                  setOpen(false);
                }}
                className="text-[11px] hover:underline"
                style={{ color: 'var(--danger)' }}
              >
                {t('vault_ui.filter.clear_all')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
